import React from "react";
import IndustryPage from "../components/IndustryPage";
import { Camera, Cpu, LineChart, Sprout } from "lucide-react";

export default function AgricultureQC({ onRequestDemo }) {
  return (
    <IndustryPage
      onRequestDemo={onRequestDemo}
      pill="AGRICULTURE"
      title="AGRICULTURAL"
      titleAccent="QUALITY CONTROL"
      tagline="SEE EVERY LEAF. GRADE EVERY YIELD."
      description="StrainScan Pro's computer-vision engine adapts to any crop — detecting disease, ripeness, pest damage, and yield anomalies from field-level drone imagery to greenhouse close-ups. Give your agronomists eyes on every plant, all season long."
      heroImage="https://images.unsplash.com/photo-1464226184884-fa280b87c399?auto=format&fit=crop&w=1600&q=85"
      heroLabel="CROP ANALYSIS"
      heroScore="96.4%"
      sideStats={[
        { label: "CROP HEALTH", value: "OPTIMAL", sub: "94.1% VIGOR" },
        { label: "RIPENESS", value: "R4 · READY", sub: "HARVEST NOW" },
        { label: "SCAN ID", value: "AG-2025-0714", sub: "FIELD 3B" },
      ]}
      howSteps={[
        { icon: <Camera size={22} />, title: "CAPTURE", desc: "Drone, phone, or fixed-camera capture at scale — from single plants to 1,000-acre flyovers." },
        { icon: <Cpu size={22} />, title: "AI ANALYSIS", desc: "Detects blight, mildew, pest damage, ripeness stage, nutrient deficiency, and yield estimates." },
        { icon: <LineChart size={22} />, title: "TREND & ALERT", desc: "Season-over-season trend data with real-time alerts when disease markers cross threshold." },
        { icon: <Sprout size={22} />, title: "AGRONOMIST-READY", desc: "Delivers actionable prescriptions to spray, harvest, or intervene — per-plant or per-block." },
      ]}
      capabilities={[
        { title: "DISEASE DETECTION", desc: "Identifies 40+ common crop diseases including powdery mildew, blight, rust, and mosaic virus at pre-symptomatic stages." },
        { title: "RIPENESS GRADING", desc: "Estimates fruit and flower maturity on a standardized 1–10 scale so harvest crews pick at peak value." },
        { title: "PEST IDENTIFICATION", desc: "Recognizes 200+ agricultural pests from a single leaf photo — even eggs and early-instar larvae." },
        { title: "NUTRIENT DEFICIENCY", desc: "Flags nitrogen, potassium, and micronutrient shortages from foliage color and pattern analysis." },
        { title: "YIELD ESTIMATION", desc: "Projects per-hectare yield with ±4% accuracy so buyers, packers, and logistics teams can plan ahead." },
        { title: "DRONE INTEGRATION", desc: "Runs on captured imagery from any commercial drone — DJI, Parrot, senseFly, Wingtra, custom fleets." },
      ]}
      metrics={[
        { label: "DISEASE DETECTED", value: "40+", sub: "PATHOGENS" },
        { label: "PEST LIBRARY", value: "200+", sub: "SPECIES" },
        { label: "YIELD ACCURACY", value: "±4%", sub: "PER HECTARE" },
        { label: "SCAN THROUGHPUT", value: "10K/hr", sub: "CROP IMAGES" },
      ]}
      useCases={[
        { kpi: "23%", title: "LESS CROP LOSS", sub: "TARGET", desc: "Designed to reduce field loss to blight and mildew by up to 23% through early-stage disease detection." },
        { kpi: "18%", title: "HIGHER YIELD", sub: "TARGET", desc: "AI-guided harvest timing is designed to lift sell-through-grade yield by up to 18% per block." },
        { kpi: "4hrs", title: "FROM DRONE TO REPORT", desc: "From flyover to actionable prescription in under four hours, versus five days for manual scouting." },
      ]}
      quote={{
        text: "Every plant on this planet tells a story — nutrients, stress, disease, potential. We built StrainScan Pro to listen. The same intelligence that grades a delicate flower grades a corn stalk. One engine, translated to the fields that feed the world.",
        author: "Marshall Perkins",
        role: "Founder & CEO, StrainScan Pro",
      }}
    />
  );
}
